import JSZip from 'jszip';
import { Mesh } from 'three';
import { pdf } from '@react-pdf/renderer';
import type {
  ColumnFilter,
  DecoratedFile,
  DecoratedFileCollection,
  DecoratedFileCollectionFile,
  DecoratedNoteFile,
  Dictionary,
  Directory,
  FileTypeId,
  RequestPagination,
  SelectedFile,
  UndecoratedFile,
  UndecoratedFileVersion,
} from '@witmetrics/utils';
import {
  COMPARISON_OPERATORS,
  FILE_TYPES,
  UPLOAD_STATUSES,
} from '@witmetrics/utils/constants';
import {
  asyncMap,
  findById,
  sortByKey,
  toDictionary,
} from '@witmetrics/utils/arrays';
import { buildColumnFilter } from '@witmetrics/utils/filters';
import { TREE_ITEM_TYPES } from '@witmetrics/unison-core';
import {
  buildPathString,
  inflateBlob,
  isImage,
  isPdf,
  isScan,
  isSweethSequence,
  isVideo,
} from '@witmetrics/utils/files';
import type {
  FetchFileCollectionFilesResponse,
  FetchFileCollectionPathResponse,
  FetchFileCollectionsResponse,
  FetchFileNotesResponse,
  FetchPracticeFileCollectionsResponse,
} from '@witmetrics/api-client';
import { generateUuid } from '@witmetrics/utils/uuid';
import { isEmpty } from '@witmetrics/utils/misc';
import { isBlank } from '@witmetrics/utils/strings';
import { VIEWER_CONTENT_TYPE } from '@/constants/viewerContent';
import type { ViewerContent } from '@/types/viewers';
import { createPreviewImage } from '@/utils/images';
import { createScanPreview, createSweethSequencePreview } from '@/utils/scans';
import type { LoadedFile } from '@/providers/LoadedFilesProvider';
import { API } from '@/api';
import { createPdfPreview } from '@/utils/pdfs';

const { IDLE } = UPLOAD_STATUSES;

export function getFileTypeLabel(fileTypeId?: FileTypeId | null) {
  if (!fileTypeId) return 'Other';
  return findById(Object.values(FILE_TYPES), fileTypeId)?.label || 'Other';
}

type BaseFile = {
  id: number;
  name: string;
};

type LoadedFiles = Dictionary<LoadedFile>;

export async function uploadFile(
  file: File,
  practiceId: number,
  fileTypeId?: FileTypeId
): Promise<{ file: UndecoratedFile; fileVersion: UndecoratedFileVersion }> {
  const newFile = await API.Files.createNewFile({
    name: file.name,
    practiceId,
    fileTypeId,
  });
  let fileVersion = await API.Files.uploadFileVersion({
    name: file.name,
    fileId: newFile.id,
    fileVersion: file,
  });
  if (isImage(file.name)) {
    const preview = await createPreviewImage(URL.createObjectURL(file));
    fileVersion = await API.Files.uploadPreview(fileVersion.id, preview);
  } else if (isScan(file.name)) {
    const preview = await createScanPreview(file, { fileTypeId });
    fileVersion = await API.Files.uploadPreview(fileVersion.id, preview);
  } else if (isPdf(file.name)) {
    const preview = await createPdfPreview(file);
    fileVersion = await API.Files.uploadPreview(fileVersion.id, preview);
  } else if (fileTypeId && isSweethSequence({ fileTypeId })) {
    const preview = await createSweethSequencePreview(file);
    fileVersion = await API.Files.uploadPreview(fileVersion.id, preview);
  }
  return { file: newFile, fileVersion };
}

export async function ensurePreview(
  fileVersion: UndecoratedFileVersion,
  fileTypeId: UndecoratedFile['fileTypeId'] | undefined,
  source: ArrayBuffer
): Promise<{ updatedVersion: UndecoratedFileVersion; preview: Blob } | null> {
  if (fileVersion.previewSource) return null;
  let preview: Blob | undefined;
  if (isImage(fileVersion.name)) {
    preview = await createPreviewImage(URL.createObjectURL(new Blob([source])));
  } else if (isScan(fileVersion.name)) {
    preview = await createScanPreview(new File([source], fileVersion.name), {
      fileTypeId,
    });
  } else if (isPdf(fileVersion.name)) {
    preview = await createPdfPreview(new Blob([source]));
  } else if (fileTypeId && isSweethSequence({ fileTypeId })) {
    preview = await createSweethSequencePreview(new Blob([source]));
  }
  if (!preview) return null;
  const updatedVersion = await API.Files.uploadPreview(fileVersion.id, preview);
  return { updatedVersion, preview };
}

export async function uploadNoteAttachment(
  noteId: number,
  file: File
): Promise<DecoratedNoteFile> {
  let noteFile = await API.Notes.addFileToNote(noteId, file);
  if (isEmpty(noteFile.file.versions)) {
    throw new Error('No file version found for NoteFile');
  }
  const preview = await createPreviewImage(URL.createObjectURL(file));
  const { previewSource } = await API.Files.uploadPreview(
    noteFile.file.versions[0].id,
    preview
  );
  noteFile.file.versions[0].previewSource = previewSource;
  return noteFile;
}

export async function createFileCollection(
  name: string,
  directory: Directory,
  practiceId: number,
  parentFileCollectionId?: number
): Promise<DecoratedFileCollection> {
  const newCollection = await API.FileCollections.createNewFileCollection({
    name,
    practiceId,
    parentFileCollectionId,
  });
  let childFiles = 0;
  let childFileCollections = 0;
  Object.keys(directory).forEach((key) => {
    if (directory[key] instanceof File) childFiles++;
    else childFileCollections++;
  });
  return {
    ...newCollection,
    childFiles,
    childFileCollections,
  };
}

export async function uploadFileToFileCollection(
  newFile: File,
  practiceId: number,
  fileCollectionId: number,
  fileTypeId?: FileTypeId
): Promise<{
  file: UndecoratedFile;
  fileCollectionFile: DecoratedFileCollectionFile;
  fileVersion: UndecoratedFileVersion;
  buffer: ArrayBuffer;
}> {
  const [{ file, fileVersion }, buffer] = await Promise.all([
    uploadFile(newFile, practiceId, fileTypeId),
    newFile.arrayBuffer(),
  ]);
  const fileCollectionFile = await API.FileCollections.addFileCollectionFile(
    fileCollectionId,
    file.id
  );
  return { file, fileCollectionFile, fileVersion, buffer };
}

export type UploadedFileData = Awaited<
  ReturnType<typeof uploadFileToFileCollection>
>;

export type DirectoryUploadHandlers = {
  onFileUploaded: (data: UploadedFileData, isTopLevel: boolean) => void;
  onCollectionCreated: (
    fileCollection: DecoratedFileCollection,
    isTopLevel: boolean
  ) => void;
  onError: (err: unknown) => void;
};

// eslint-disable-next-line witmetrics/no-multiline-comments
/**
 * Recursively uploads a picked or dropped directory tree: each nested folder
 * becomes a file collection, each file is uploaded into its collection. Entries
 * directly under `rootParentFileCollectionId` are flagged `isTopLevel` so callers
 * can run their "added to this collection" side effects. Errors are reported per
 * node via `onError` and do not abort sibling uploads. Consolidates the directory
 * upload logic that previously lived inline in NewFileMenuButton.
 */
export function uploadDirectoryTree(
  tree: Directory,
  practiceId: number,
  rootParentFileCollectionId: number | undefined,
  handlers: DirectoryUploadHandlers,
  concurrency = 10
): Promise<void> {
  return uploadDirectoryEntries(
    tree,
    practiceId,
    rootParentFileCollectionId,
    rootParentFileCollectionId,
    handlers,
    createConcurrencyLimiter(concurrency)
  );
}

async function uploadDirectoryEntries(
  entries: Directory,
  practiceId: number,
  parentFileCollectionId: number | undefined,
  rootParentFileCollectionId: number | undefined,
  handlers: DirectoryUploadHandlers,
  limit: Limiter
): Promise<void> {
  const isTopLevel = parentFileCollectionId === rootParentFileCollectionId;
  await asyncMap(Object.keys(entries), async (name) => {
    try {
      const contents = entries[name];
      if (contents instanceof File) {
        if (!parentFileCollectionId) {
          throw Error('Missing required parentFileCollectionId');
        }
        const collectionId = parentFileCollectionId;
        const data = await limit(() =>
          uploadFileToFileCollection(contents, practiceId, collectionId)
        );
        handlers.onFileUploaded(data, isTopLevel);
      } else {
        const fileCollection = await limit(() =>
          createFileCollection(
            name,
            contents,
            practiceId,
            parentFileCollectionId
          )
        );
        handlers.onCollectionCreated(fileCollection, isTopLevel);
        await uploadDirectoryEntries(
          contents,
          practiceId,
          fileCollection.id,
          rootParentFileCollectionId,
          handlers,
          limit
        );
      }
    } catch (err) {
      handlers.onError(err);
    }
  });
}

type Limiter = <T>(task: () => Promise<T>) => Promise<T>;

// eslint-disable-next-line witmetrics/no-multiline-comments
/**
 * Minimal concurrency limiter (no equivalent exists in @witmetrics/utils): runs
 * at most `concurrency` tasks at once, pulling the next queued task as each
 * settles. Without it a large directory upload reads every file into memory and
 * fires every request at the server in a single burst.
 */
function createConcurrencyLimiter(concurrency: number): Limiter {
  let active = 0;
  const queue: (() => void)[] = [];
  const drain = () => {
    while (active < concurrency && queue.length > 0) {
      active += 1;
      queue.shift()!();
    }
  };
  return <T>(task: () => Promise<T>) =>
    new Promise<T>((resolve, reject) => {
      queue.push(() =>
        task()
          .then(resolve, reject)
          .finally(() => {
            active -= 1;
            drain();
          })
      );
      drain();
    });
}

export async function fetchCollectionContents(
  fileCollectionId: number,
  { page, pageSize }: RequestPagination = { page: 1, pageSize: 100 }
): Promise<{
  fileCollections: FetchFileCollectionsResponse;
  files: FetchFileCollectionFilesResponse;
  path: FetchFileCollectionPathResponse;
}> {
  // TODO: pagination for collections and files should be separate
  const [fileCollections, files, path] = await Promise.all([
    fetchChildFileCollections(fileCollectionId, { page, pageSize }),
    fetchFiles(fileCollectionId, { page, pageSize }),
    API.FileCollections.fetchFileCollectionPath(fileCollectionId),
  ]);
  return {
    fileCollections,
    files,
    path: sortByKey(path, 'directoryDepth'),
  };
}

export function fetchChildFileCollections(
  fileCollectionId: number,
  { page, pageSize }: RequestPagination
): Promise<FetchFileCollectionsResponse> {
  return API.FileCollections.fetchFileCollections({
    pagination: { page, pageSize },
    filters: [buildParentFilter(fileCollectionId)],
  });
}

export function fetchFiles(
  fileCollectionId: number,
  { page, pageSize }: RequestPagination
): Promise<FetchFileCollectionFilesResponse> {
  return API.FileCollections.fetchFileCollectionFiles(fileCollectionId, {
    filters: [],
    pagination: { page, pageSize },
  });
}

function buildParentFilter(
  fileCollectionId: number | null = null
): ColumnFilter {
  return buildColumnFilter(
    'parentFileCollectionId',
    COMPARISON_OPERATORS.EQUALS.value,
    fileCollectionId
  );
}

export function fetchPracticeFileCollections(
  practiceId: number,
  { page, pageSize }: RequestPagination
): Promise<FetchPracticeFileCollectionsResponse> {
  return API.Practices.fetchPracticeFileCollections(practiceId, {
    pagination: { page, pageSize },
    filters: [buildParentFilter(null)],
  });
}

export async function fetchFileVersion(
  fileId: number
): Promise<UndecoratedFileVersion | null> {
  const fileVersions = await API.Files.fetchFileVersions(fileId, {
    filters: [],
    pagination: {
      page: 1,
      pageSize: 10, // Should realistically only be 1 file version
    },
  });
  return fileVersions?.results?.length > 0 ? fileVersions.results[0] : null;
}

export async function downloadFileVersion(
  fileVersionId: number,
  onDownloadProgress?: Parameters<typeof API.Files.downloadFileVersion>[1]
): Promise<ArrayBufferLike> {
  const result = await API.Files.downloadFileVersion(
    fileVersionId,
    onDownloadProgress
  );
  return inflateBlob(result);
}

export async function downloadPreview(
  fileVersionId: number
): Promise<ArrayBufferLike> {
  const result = await API.Files.downloadPreview(fileVersionId);
  return inflateBlob(result);
}

export async function downloadSharedFile(
  fileId: number,
  token: string
): Promise<ArrayBufferLike> {
  const result = await API.SharedContent.downloadSharedFile(fileId, token);
  return inflateBlob(result);
}

export function downloadBlob(blob: Blob, filename: string): string {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return url;
}

export async function openFileDownloadLink(
  fileVersionId: number,
  filename: string
): Promise<string> {
  const inflated = await downloadFileVersion(fileVersionId);
  const blob = new Blob([inflated], { type: 'application/octet-stream' });
  return downloadBlob(blob, filename);
}

export async function openSharedFileDownloadLink(
  fileId: number,
  filename: string,
  token: string
): Promise<string> {
  const inflated = await downloadSharedFile(fileId, token);
  const blob = new Blob([inflated], { type: 'application/octet-stream' });
  return downloadBlob(blob, filename);
}

export async function fetchFileCollectionData(
  fileCollectionIds: number[]
): Promise<{
  fileCollections: DecoratedFileCollection[];
  files: DecoratedFileCollectionFile[];
}> {
  let queue: number[] = [...fileCollectionIds];
  let files: DecoratedFileCollectionFile[] = [];
  let fileCollections: DecoratedFileCollection[] = [];
  // TODO: Pagination
  while (queue.length > 0) {
    const currentId = queue.shift();
    if (!currentId) continue;
    const data = await fetchCollectionContents(currentId);
    files.push(...data.files.results);
    fileCollections.push(...data.fileCollections.results);
    data.fileCollections.results.forEach(({ id }) => queue.push(id));
  }
  return {
    fileCollections,
    files,
  };
}

export async function downloadFileCollection(
  fileCollectionId: number,
  onProgress: (update: string) => void
): Promise<void> {
  const zip = new JSZip();
  onProgress('Fetching contents...');
  const [fileCollection, data] = await Promise.all([
    API.FileCollections.fetchFileCollection(fileCollectionId),
    fetchFileCollectionData([fileCollectionId]),
  ]);
  const fileCollections = toDictionary(data.fileCollections, 'id');
  onProgress('Downloading files...');
  const files = await asyncMap(data.files, loadFileCollectionFile);
  onProgress('Preparing download...');
  for (const file of files) {
    const folder = buildPathString(
      file.fileCollectionId,
      fileCollections,
      fileCollectionId
    );
    let path = `${isBlank(folder) ? '' : `${folder}/`}${file.name}`;
    if (path.charAt(0) === '/') path = path.slice(1);
    zip.file(path, file.data);
  }
  const blob = await zip.generateAsync({ type: 'blob' });
  downloadBlob(blob, `${fileCollection.name}.zip`);
}

async function loadFileCollectionFile({
  fileId,
  fileCollectionId,
}: DecoratedFileCollectionFile): Promise<{
  fileCollectionId: number;
  name: string;
  data: ArrayBufferLike;
}> {
  const fileVersion = await fetchFileVersion(fileId);
  if (!fileVersion) throw Error(`Unknown fileVersionId: ${fileId}`);
  const inflated = await downloadFileVersion(fileVersion.id);
  return {
    fileCollectionId,
    name: fileVersion.name,
    data: inflated,
  };
}

export function getLoadedScan(
  file: BaseFile | undefined,
  loadedFiles: LoadedFiles
): Mesh | null {
  if (!file) return null;
  const loadedFile = loadedFiles[file.id];
  return loadedFile instanceof Mesh ? loadedFile : null;
}

export function getLoadedSweethSequence(
  file: Pick<UndecoratedFile, 'id' | 'fileTypeId'>,
  loadedFiles: LoadedFiles
): Blob | null {
  const loadedFile = loadedFiles[file.id] as Blob;
  return isSweethSequence(file) && loadedFile ? loadedFile : null;
}

export function getLoadedImage(
  file: BaseFile,
  loadedFiles: LoadedFiles
): string | null {
  const src = loadedFiles[file.id] as string | null;
  return !!src && isImage(file.name) ? src : null;
}

export function getLoadedPdf(
  file: BaseFile,
  loadedFiles: LoadedFiles
): Blob | null {
  const src = loadedFiles[file.id] as Blob | null;
  return !!src && isPdf(file.name) ? src : null;
}

export function getLoadedVideo(
  file: BaseFile,
  loadedFiles: LoadedFiles
): string | null {
  const src = loadedFiles[file.id] as string | null;
  return !!src && isVideo(file.name) ? src : null;
}

export function formatTreeId(
  id: number,
  type: keyof typeof TREE_ITEM_TYPES
): string {
  if (type === TREE_ITEM_TYPES.FILE) return `file.${id}`;
  else return `fileCollection.${id}`;
}

export function parseCompositeId(id: string): number {
  return parseInt(id.split('.')[1]);
}

export function buildFileContent(
  fileId: number,
  showTimeline = false
): ViewerContent {
  return { id: fileId, showTimeline, contentType: VIEWER_CONTENT_TYPE.FILE };
}

export function buildFileCollectionContent(
  fileCollectionId: number,
  showTimeline = false
): ViewerContent {
  return {
    id: fileCollectionId,
    showTimeline,
    contentType: VIEWER_CONTENT_TYPE.FILE_COLLECTION,
  };
}

export async function fetchFileData(fileId: number) {
  const [file, fileVersion, fileNotes] = await Promise.all([
    API.Files.fetchFile(fileId),
    fetchFileVersion(fileId),
    (
      await API.Files.fetchFileNotes(fileId, {
        pagination: { page: 1, pageSize: 100 }, // TODO: Check for > 1 page of results
      })
    ).results,
  ]);
  return { file, fileVersion, fileNotes };
}

export function fetchFileNotes(
  fileId: number,
  { page, pageSize }: RequestPagination
): Promise<FetchFileNotesResponse> {
  return API.Files.fetchFileNotes(fileId, {
    pagination: { page, pageSize },
  });
}

export async function buildSelectedFile(file: File): Promise<SelectedFile> {
  let previewFile: Blob | undefined;
  if (isImage(file.name)) {
    previewFile = await createPreviewImage(URL.createObjectURL(file));
  } else if (isScan(file.name)) {
    previewFile = await createScanPreview(file);
  }
  return {
    uuid: generateUuid(),
    uploadStatus: IDLE,
    file,
    previewFile,
    source: URL.createObjectURL(file),
    previewSource: previewFile ? URL.createObjectURL(previewFile) : undefined,
  };
}

export function getFileName(file: DecoratedFile) {
  // Sweeth Sequences are always .zips, no need to show it
  return isSweethSequence(file) ? file.name.replace(/\.zip$/, '') : file.name;
}

export async function buildPdfFile(
  name: string,
  pdfNode: Parameters<typeof pdf>[0]
) {
  const blob = await pdf(pdfNode).toBlob();
  return new File([blob], `${name}.pdf`, { type: blob.type });
}
